<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable
      ref="rtable"
      :columns="columnDefs"
      :settings="{
        theme: 'ag-theme-alpine',
        title: t('allItemsReport'),
        hideFilter: true,
        customRowsKey: 'items'
      }"
      endpoint="reports/inventory/all-items"
      @onDataLoaded="onDataLoaded"
    >
      <template #filter>
        <!-- Row 1: Location, Category, Brand -->
        <el-select
          class="col-span-12 md:col-span-3"
          v-model="filters.location_id"
          :placeholder="t('selectLocation')"
          clearable
          filterable
          remote
          :remote-method="searchLocation"
          @change="onFilterChange"
        >
          <el-option
            v-for="loc in locations"
            :key="loc.id"
            :label="$ctx.locale === 'en' ? loc.enName : loc.arName"
            :value="loc.id"
          />
        </el-select>

        <el-select
          class="col-span-12 md:col-span-3"
          v-model="filters.category_id"
          :placeholder="t('category')"
          clearable
          filterable
          remote
          :remote-method="searchCategory"
          @change="onFilterChange"
        >
          <el-option
            v-for="cat in categories"
            :key="cat.id"
            :label="cat.name"
            :value="cat.id"
          />
        </el-select>

        <el-select
          class="col-span-12 md:col-span-3"
          v-model="filters.brand_id"
          :placeholder="t('brand')"
          clearable
          filterable
          remote
          :remote-method="searchBrand"
          @change="onFilterChange"
        >
          <el-option
            v-for="brand in brands"
            :key="brand.id"
            :label="brand.name"
            :value="brand.id"
          />
        </el-select>
      </template>

      <template #filter-bottom>
        <div class="grid grid-cols-12 gap-2">
          <el-select
            class="col-span-12 md:col-span-2"
            v-model="filters.vendor_id"
            :placeholder="t('vendor')"
            clearable
            filterable
            remote
            :remote-method="searchVendor"
            @change="onFilterChange"
          >
            <el-option
              v-for="vendor in vendors"
              :key="vendor.id"
              :label="vendor.name"
              :value="vendor.id"
            />
          </el-select>

          <el-select
            class="col-span-12 md:col-span-2"
            v-model="filters.tag_id"
            :placeholder="t('tag')"
            clearable
            filterable
            remote
            :remote-method="searchTag"
            @change="onFilterChange"
          >
            <el-option
              v-for="tag in tags"
              :key="tag.id"
              :label="tag.name"
              :value="tag.id"
            />
          </el-select>

          <el-select
            class="col-span-12 md:col-span-2"
            v-model="filters.attribute_id"
            :placeholder="t('attribute')"
            clearable
            filterable
            remote
            :remote-method="searchAttribute"
            @change="onFilterChange"
          >
            <el-option
              v-for="attr in attributes"
              :key="attr.id"
              :label="$ctx.locale === 'en' ? attr.enName : attr.arName"
              :value="attr.id"
            />
          </el-select>
          

          <money-input
            v-model="filters.min_price"
            :placeholder="t('minPrice')"
            class="col-span-12 md:col-span-3"
            currency="IQD"
          />

          <money-input
            v-model="filters.max_price"
            :placeholder="t('maxPrice')"
            class="col-span-12 md:col-span-3"
            currency="IQD"
          />
        </div>
      </template>

      <template #cards="{ data }">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 no-print">
          <!-- Total Items Card -->
          <div class="bg-white rounded shadow p-4">
            <p class="text-gray-600 text-sm">{{ t('totalItems') }}</p>
            <p class="text-2xl font-bold">{{ data?.summary?.totalItems ?? '-' }}</p>
          </div>

          <!-- Total Quantity Card -->
          <div class="bg-white rounded shadow p-4">
            <p class="text-gray-600 text-sm">{{ t('totalQuantity') }}</p>
            <p class="text-2xl font-bold">{{ data?.summary?.totalQuantity ?? '-' }}</p>
          </div>

          <!-- Total Value Card -->
          <div class="bg-white rounded shadow p-4">
            <p class="text-gray-600 text-sm">{{ t('totalValue') }}</p>
            <p class="text-xl font-bold">
              <template v-if="data?.summary?.totalValue !== undefined">
                {{ formatCurrency(data.summary.totalValue, data.summary.precision) }}
                <span class="text-xs font-normal ml-1">{{ data.summary.currency }}</span>
              </template>
              <template v-else>-</template>
            </p>
          </div>
        </div>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import { onBeforeMount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { ColDef } from 'ag-grid-community'
import { useHTTP } from '@/plugins/http'
import { useContext } from '@/plugins/context'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import MoneyInput from '@/components/form/MoneyInput.vue'
import Location from '@/models/company/Location'
import Category from '@/models/inventory/Category'
import Brand from '@/models/inventory/Brand'
import Vendor from '@/models/vendor/Vendor'
import Tag from '@/models/inventory/Tag'
import Attribute from '@/models/inventory/Attribute'

// i18n
const { t } = useI18n({
  messages: {
    en: {
      allItemsReport: 'All Items Report',
      selectLocation: 'Select Location',
      category: 'Category',
      brand: 'Brand',
      vendor: 'Vendor',
      tag: 'Tag',
      attribute: 'Attribute',
      minPrice: 'Min Price',
      maxPrice: 'Max Price',
      totalItems: 'Total Items',
      totalQuantity: 'Total Quantity',
      totalValue: 'Total Value',
      itemName: 'Item Name',
      quantity: 'Quantity'
    },
    ar: {
      allItemsReport: 'تقرير جميع المواد',
      selectLocation: 'اختر الموقع',
      category: 'الفئة',
      brand: 'العلامة التجارية',
      vendor: 'المورد',
      tag: 'الوسم',
      attribute: 'السمة',
      minPrice: 'السعر الأدنى',
      maxPrice: 'السعر الأقصى',
      totalItems: 'إجمالي المواد',
      totalQuantity: 'الكمية الإجمالية',
      totalValue: 'القيمة الإجمالية',
      itemName: 'اسم المادة',
      quantity: 'الكمية'
    }
  }
})

const ctx = useContext()
const http = useHTTP()

// Data
const rtable = ref<any>()
const columnDefs = ref<ColDef[]>([])

const filters = ref({
  location_id: '',
  category_id: '',
  brand_id: '',
  vendor_id: '',
  tag_id: '',
  attribute_id: '',
  min_price: undefined as number | undefined,
  max_price: undefined as number | undefined
})

const locations = ref<Location[]>([])
const categories = ref<any[]>([])
const brands = ref<any[]>([])
const vendors = ref<any[]>([])
const tags = ref<any[]>([])
const attributes = ref<any[]>([])

// Watch for price changes and trigger filter
watch(() => filters.value.min_price, () => {
  onFilterChange()
})

watch(() => filters.value.max_price, () => {
  onFilterChange()
})

onBeforeMount(async () => {
  // Define columns - only item name and quantity
  columnDefs.value = [
    {
      headerName: t('itemName'),
      field: 'itemName',
      checkboxSelection: true,
      headerCheckboxSelection: true,
      autoHeight: true,
      cellStyle: { textAlign: 'start' },
      minWidth: 250
    },
    {
      headerName: t('quantity'),
      field: 'quantity',
      type: 'numericColumn',
      filter: 'agNumberColumnFilter'
    }
  ]

  // Load initial filter options
  await loadFilterOptions()
})

async function loadFilterOptions() {
  // Load initial data with empty query
  await Promise.all([
    searchLocation(''),
    searchCategory(''),
    searchBrand(''),
    searchVendor(''),
    searchTag(''),
    searchAttribute('')
  ])
}

async function searchLocation(query: string) {
  if (!query) query = ''
  try {
    const url = `${Location.ENDPOINT}?q=${query}`
    const res = await http.get<Location[]>(url)
    locations.value = res.map((loc) => Location.from(loc))
  } catch (error) {
    locations.value = []
  }
}

async function searchCategory(query: string) {
  if (!query) query = ''
  try {
    const url = `${Category.ENDPOINT}?q=${query}`
    const res = await http.get<any[]>(url)
    categories.value = res
  } catch (error) {
    categories.value = []
  }
}

async function searchBrand(query: string) {
  if (!query) query = ''
  try {
    const url = `${Brand.ENDPOINT}?q=${query}`
    const res = await http.get<any[]>(url)
    brands.value = res
  } catch (error) {
    brands.value = []
  }
}

async function searchVendor(query: string) {
  if (!query) query = ''
  try {
    const url = `${Vendor.ENDPOINT}?q=${query}`
    const res = await http.get<any[]>(url)
    vendors.value = res
  } catch (error) {
    vendors.value = []
  }
}

async function searchTag(query: string) {
  if (!query) query = ''
  try {
    const url = `${Tag.ENDPOINT}?q=${query}`
    const res = await http.get<any[]>(url)
    tags.value = res
  } catch (error) {
    tags.value = []
  }
}

async function searchAttribute(query: string) {
  if (!query) query = ''
  try {
    const url = `${Attribute.ENDPOINT}?q=${query}`
    const res = await http.get<any[]>(url)
    attributes.value = res
  } catch (error) {
    attributes.value = []
  }
}

function onDataLoaded(data: any) {
  // Handle data loaded event if needed
}

function onFilterChange() {
  if (!rtable.value) return

  // Build filter object - only include non-empty values
  const filterParams: any = {}

  if (filters.value.location_id) filterParams.location_id = filters.value.location_id
  if (filters.value.category_id) filterParams.category_id = filters.value.category_id
  if (filters.value.brand_id) filterParams.brand_id = filters.value.brand_id
  if (filters.value.vendor_id) filterParams.vendor_id = filters.value.vendor_id
  if (filters.value.tag_id) filterParams.tag_id = filters.value.tag_id
  if (filters.value.attribute_id) filterParams.attribute_id = filters.value.attribute_id

  // Add price filters if they have values
  if (typeof filters.value.min_price === 'number' && filters.value.min_price > 0) {
    filterParams.min_price = filters.value.min_price
  }
  if (typeof filters.value.max_price === 'number' && filters.value.max_price > 0) {
    filterParams.max_price = filters.value.max_price
  }

  rtable.value.payload = {
    location_id: null,
    period: rtable.value.payload.period || null,
    from_date: rtable.value.payload.from_date || null,
    to_date: rtable.value.payload.to_date || null,
    filter: rtable.value.payload.filter || null,
    ...filterParams
  }
  rtable.value.navigate(rtable.value.payload)
}

function formatCurrency(value: number, precision: number) {
  if (!value) return '0'
  const divisor = Math.pow(10, precision)
  return (value / divisor).toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision
  })
}
</script>

<style scoped>
.ag-theme-alpine {
  --ag-background-color: white;
  --ag-border-color: #e5e7eb;
  --ag-row-hover-color: #f3f4f6;
}
</style>
